import {useState} from "react";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {ChevronUpIcon} from "@pg-design/icons-module";
import {Image, Placeholder} from "@pg-design/image-module";
import {Text} from "@pg-design/text-module";
import {CREDIT_PAGES_IDS} from "@pg-mono/e2e-test-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {CreditApplicationSource} from "../../../application/utils/CreditApplicationSource";
import {useCreditPaywall} from "../../hooks/use_credit_paywall";
import {creditCalculatorAnalytics, CreditCalculatorGTMEvent, CreditCalculatorListingActions} from "../../tracking/credit_gtm";
import {ICreditData} from "../../types/ICreditData";
import {ICreditOffer} from "../../types/ICreditOffer";
import {getBankLogo} from "../../utils/get_bank_logo";
import {getSecondaryDataFromOffer} from "../../utils/get_secondary_data_from_offer";
import {replaceDigitsWithZeros} from "../../utils/replace_digits_with_zeros";
import {CreditOfferBoxMainData, IMainDataProps} from "./CreditOfferBoxMainData";

interface IProps {
    offer: ICreditOffer;
    onButtonClick: (payload: ICreditData) => void;
}

const YEAR_IN_MONTHS = 12;

export const CreditOfferBox = (props: IProps) => {
    const {offer, onButtonClick} = props;

    const [showDetails, setShowDetails] = useState(false);
    const {isMobile} = useUserDevice();
    const {areOffersUnlocked} = useCreditPaywall();

    const logo = getBankLogo(offer.bank);
    const offerName = offer.offer_name.split(" - ")[0];
    const shouldBlur = !areOffersUnlocked;
    const mainDataProps: IMainDataProps = {
        fixedInterestPercent: offer.fixed_interest_rate_percent,
        commission: offer.commission,
        equalInstallmentRate: offer.equal_installment_rate,
        rrso: offer.rrso,
        shouldBlur
    };

    const moreData = getSecondaryDataFromOffer(offer);

    const onClick = (source: CreditApplicationSource) => {
        const payload: ICreditData = {
            source: source,
            term_of_the_loan: offer.maximum_term_of_the_loan_in_months / YEAR_IN_MONTHS,
            bank: offer.bank,
            mortgage_offer_name: offerName,
            credit_amount: Math.ceil(offer.max_loan_price)
        };

        onButtonClick(payload);
    };

    const onShowDetails = () => {
        creditCalculatorAnalytics.gtm.creditCalculatorEvent(CreditCalculatorGTMEvent.CREDIT_INSTALMENT_CALCULATOR, {
            credit_action: showDetails ? CreditCalculatorListingActions.HIDE_DETAILS : CreditCalculatorListingActions.SHOW_DETAILS
        });
        setShowDetails(!showDetails);
    };

    const renderButtons = () => {
        // Show single button only when data is locked
        if (!areOffersUnlocked) {
            return (
                <div className={buttonsHolderStyle}>
                    <Button
                        variant="filled_primary"
                        size="x-small"
                        onClick={() => onClick(CreditApplicationSource.MORTGAGE_APPLICATION_SOURCE_CREDIT)}
                        className={btnStyle}
                    >
                        <Text variant="button_small" dataTestId={CREDIT_PAGES_IDS.COMPARE_CREDITS_SUBPAGE.CREDIT_OFFER_REQUEST_OPEN_BUTTON}>
                            Odkryj warunki kredytu
                        </Text>
                    </Button>
                </div>
            );
        }

        // Show two buttons when data is unlocked
        return (
            <div className={buttonsHolderStyle}>
                <Button
                    variant="filled_primary"
                    size="x-small"
                    onClick={() => onClick(CreditApplicationSource.MORTGAGE_APPLICATION_SOURCE_CREDIT)}
                    className={cx(btnStyle, btnMarginStyle)}
                >
                    <Text variant="button_small" dataTestId={CREDIT_PAGES_IDS.COMPARE_CREDITS_SUBPAGE.CREDIT_OFFER_REQUEST_OPEN_BUTTON}>
                        Zapytaj o kredyt
                    </Text>
                </Button>
                <Button
                    variant="outlined_secondary"
                    size="x-small"
                    onClick={() => onClick(CreditApplicationSource.MORTGAGE_APPLICATION_SOURCE_CREDIT_RATING)}
                    className={btnStyle}
                >
                    <Text variant="button_small" dataTestId={CREDIT_PAGES_IDS.COMPARE_CREDITS_SUBPAGE.CREDITWORTHINESS_FORM_OPEN_BUTTON}>
                        Sprawdź zdolność kredytową
                    </Text>
                </Button>
            </div>
        );
    };

    return (
        <div className={boxWrapperStyle}>
            <div className={boxHeaderWrapperStyle}>
                {logo ? (
                    <Image src={logo} alt="Kalkulator" width="96" height="96" className={imageStyle} />
                ) : (
                    <div className={placeholderWrapperStyle}>
                        <Placeholder />
                    </div>
                )}
                <div className={boxHeaderTextsStyle}>
                    <Text variant="headline_3" dataTestId={CREDIT_PAGES_IDS.COMPARE_CREDITS_SUBPAGE.CREDIT_TITLE_BOX}>
                        {offerName}
                    </Text>
                    <Text as="div" variant="headline_3" color="var(--colors-gray-600)" dataTestId={CREDIT_PAGES_IDS.COMPARE_CREDITS_SUBPAGE.BANK_NAME_BOX}>
                        {offer.bank}
                    </Text>
                </div>
            </div>

            <CreditOfferBoxMainData {...mainDataProps} />

            <div className={buttonsWrapperStyle}>
                {!isMobile && renderButtons()}

                <div className={detailsButtonStyle} onClick={onShowDetails}>
                    <Text as="div" variant="button_medium" className={detailsTextStyle}>
                        szczegóły oferty
                    </Text>
                    <div className={showDetails ? detailsArrowIconVisibleStyle : detailsArrowIconHiddenStyle}>
                        <ChevronUpIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" />
                    </div>
                </div>
            </div>

            <div className={cx(secondaryDataWrapperStyle, showDetails ? secondaryDataWrapperVisibleStyle : secondaryDataWrapperHiddenStyle)}>
                {moreData.map((data) => {
                    if (!data.value) {
                        return null;
                    }
                    const displayValue = shouldBlur ? replaceDigitsWithZeros(data.value) : data.value;
                    return (
                        <div key={data.title} className={secondaryDataHolderStyle}>
                            <Text variant="body_copy_2" className={dataTitleStyle}>
                                {data.title}
                            </Text>
                            <Text variant="info_txt_1" color="var(--colors-gray-800)" className={cx(dataValueStyle, shouldBlur && blurredSecondaryDataStyle)}>
                                <span dangerouslySetInnerHTML={{__html: displayValue}} />
                            </Text>
                        </div>
                    );
                })}
            </div>

            {isMobile && renderButtons()}
        </div>
    );
};

const placeholderWrapperStyle = css({
    position: "relative",
    width: "96px",
    height: "96px"
});

const boxWrapperStyle = css({
    backgroundColor: "var(--colors-white)",
    shadow: "",
    width: "100%",
    padding: 2,
    borderRadius: 2,
    md: {
        padding: 3
    }
});

const boxHeaderWrapperStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    flexDirection: "row",
    gap: 1,
    mb: 2
});

const imageStyle = css({
    minWidth: "96px"
});

const boxHeaderTextsStyle = css({
    display: "flex",
    flexDirection: "column"
});

const buttonsWrapperStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: "column-reverse",
    marginBottom: 3,
    md: {
        flexDirection: "row",
        marginBottom: 0
    }
});

const detailsButtonStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer"
});

const detailsTextStyle = css({
    marginRight: 1
});

const dataTitleStyle = css({
    md: {
        width: "33%"
    }
});

const dataValueStyle = css({
    md: {
        width: "66%"
    }
});

const secondaryDataHolderStyle = css({
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    flexDirection: "column",
    width: "100%",
    borderBottom: "1px solid var(--colors-gray-500)",
    paddingY: 1,
    paddingX: 0,
    gap: 0.5,
    md: {
        alignItems: "center",
        flexDirection: "row"
    }
});

const blurredSecondaryDataStyle = css({
    filter: "blur(6px)",
    userSelect: "none"
});

const buttonsHolderStyle = css({
    display: "inline-flex",
    flexDirection: "column",
    width: "100%",
    md: {
        flexDirection: "row",
        width: "50%"
    }
});

const btnStyle = css({
    width: "100%",
    md: {
        width: "unset"
    }
});

const btnMarginStyle = css({
    marginBottom: 2,
    md: {
        marginRight: 2,
        marginBottom: 0
    }
});

const secondaryDataWrapperStyle = css({
    transition: "max-height 0.4s ease-out",
    overflow: "hidden",
    "& > div:first-of-type": {
        marginTop: 4
    },
    "& div:last-child": {
        borderBottom: "unset",
        marginBottom: 4,
        md: {
            marginBottom: 0
        }
    }
});

const secondaryDataWrapperVisibleStyle = css({
    maxHeight: "500px"
});

const secondaryDataWrapperHiddenStyle = css({
    maxHeight: 0
});

const detailsArrowIconVisibleStyle = css({
    transform: "rotate(0deg)",
    transition: "transform 0.4s linear"
});

const detailsArrowIconHiddenStyle = css({
    transform: "rotate(180deg)",
    transition: "transform 0.4s linear"
});
import {useState} from "react";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {ChevronUpIcon} from "@pg-design/icons-module";
import {Image, Placeholder} from "@pg-design/image-module";
import {Text} from "@pg-design/text-module";
import {CREDIT_PAGES_IDS} from "@pg-mono/e2e-test-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {CreditApplicationSource} from "../../../application/utils/CreditApplicationSource";
import {useCreditPaywall} from "../../hooks/use_credit_paywall";
import {creditCalculatorAnalytics, CreditCalculatorGTMEvent, CreditCalculatorListingActions} from "../../tracking/credit_gtm";
import {ICreditData} from "../../types/ICreditData";
import {ICreditOffer} from "../../types/ICreditOffer";
import {getBankLogo} from "../../utils/get_bank_logo";
import {getSecondaryDataFromOffer} from "../../utils/get_secondary_data_from_offer";
import {replaceDigitsWithZeros} from "../../utils/replace_digits_with_zeros";
import {CreditOfferBoxMainData, IMainDataProps} from "./CreditOfferBoxMainData";

interface IProps {
    offer: ICreditOffer;
    onButtonClick: (payload: ICreditData) => void;
}

const YEAR_IN_MONTHS = 12;

export const CreditOfferBox = (props: IProps) => {
    const {offer, onButtonClick} = props;

    const [showDetails, setShowDetails] = useState(false);
    const {isMobile} = useUserDevice();
    const {areOffersUnlocked} = useCreditPaywall();

    const logo = getBankLogo(offer.bank);
    const offerName = offer.offer_name.split(" - ")[0];
    const shouldBlur = !areOffersUnlocked;
    const mainDataProps: IMainDataProps = {
        fixedInterestPercent: offer.fixed_interest_rate_percent,
        commission: offer.commission,
        equalInstallmentRate: offer.equal_installment_rate,
        rrso: offer.rrso,
        shouldBlur
    };

    const moreData = getSecondaryDataFromOffer(offer);

    const onClick = (source: CreditApplicationSource) => {
        const payload: ICreditData = {
            source: source,
            term_of_the_loan: offer.maximum_term_of_the_loan_in_months / YEAR_IN_MONTHS,
            bank: offer.bank,
            mortgage_offer_name: offerName,
            credit_amount: Math.ceil(offer.max_loan_price)
        };

        onButtonClick(payload);
    };

    const onShowDetails = () => {
        creditCalculatorAnalytics.gtm.creditCalculatorEvent(CreditCalculatorGTMEvent.CREDIT_INSTALMENT_CALCULATOR, {
            credit_action: showDetails ? CreditCalculatorListingActions.HIDE_DETAILS : CreditCalculatorListingActions.SHOW_DETAILS
        });
        setShowDetails(!showDetails);
    };

    const renderButtons = () => {
        // Show single button only when data is locked
        if (!areOffersUnlocked) {
            return (
                <div className={buttonsHolderStyle}>
                    <Button
                        variant="filled_primary"
                        size="x-small"
                        onClick={() => onClick(CreditApplicationSource.MORTGAGE_APPLICATION_SOURCE_CREDIT)}
                        className={btnStyle}
                    >
                        <Text variant="button_small" dataTestId={CREDIT_PAGES_IDS.COMPARE_CREDITS_SUBPAGE.CREDIT_OFFER_REQUEST_OPEN_BUTTON}>
                            Odkryj warunki kredytu
                        </Text>
                    </Button>
                </div>
            );
        }

        // Show two buttons when data is unlocked
        return (
            <div className={buttonsHolderStyle}>
                <Button
                    variant="filled_primary"
                    size="x-small"
                    onClick={() => onClick(CreditApplicationSource.MORTGAGE_APPLICATION_SOURCE_CREDIT)}
                    className={cx(btnStyle, btnMarginStyle)}
                >
                    <Text variant="button_small" dataTestId={CREDIT_PAGES_IDS.COMPARE_CREDITS_SUBPAGE.CREDIT_OFFER_REQUEST_OPEN_BUTTON}>
                        Zapytaj o kredyt
                    </Text>
                </Button>
                <Button
                    variant="outlined_secondary"
                    size="x-small"
                    onClick={() => onClick(CreditApplicationSource.MORTGAGE_APPLICATION_SOURCE_CREDIT_RATING)}
                    className={btnStyle}
                >
                    <Text variant="button_small" dataTestId={CREDIT_PAGES_IDS.COMPARE_CREDITS_SUBPAGE.CREDITWORTHINESS_FORM_OPEN_BUTTON}>
                        Sprawdź zdolność kredytową
                    </Text>
                </Button>
            </div>
        );
    };

    return (
        <div className={boxWrapperStyle}>
            <div className={boxHeaderWrapperStyle}>
                {logo ? (
                    <Image src={logo} alt="Kalkulator" width="96" height="96" className={imageStyle} />
                ) : (
                    <div className={placeholderWrapperStyle}>
                        <Placeholder />
                    </div>
                )}
                <div className={boxHeaderTextsStyle}>
                    <Text variant="headline_3" dataTestId={CREDIT_PAGES_IDS.COMPARE_CREDITS_SUBPAGE.CREDIT_TITLE_BOX}>
                        {offerName}
                    </Text>
                    <Text as="div" variant="headline_3" color="var(--colors-gray-600)" dataTestId={CREDIT_PAGES_IDS.COMPARE_CREDITS_SUBPAGE.BANK_NAME_BOX}>
                        {offer.bank}
                    </Text>
                </div>
            </div>

            <CreditOfferBoxMainData {...mainDataProps} />

            <div className={buttonsWrapperStyle}>
                {!isMobile && renderButtons()}

                <div className={detailsButtonStyle} onClick={onShowDetails}>
                    <Text as="div" variant="button_medium" className={detailsTextStyle}>
                        szczegóły oferty
                    </Text>
                    <div className={showDetails ? detailsArrowIconVisibleStyle : detailsArrowIconHiddenStyle}>
                        <ChevronUpIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" />
                    </div>
                </div>
            </div>

            <div className={cx(secondaryDataWrapperStyle, showDetails ? secondaryDataWrapperVisibleStyle : secondaryDataWrapperHiddenStyle)}>
                {moreData.map((data) => {
                    if (!data.value) {
                        return null;
                    }
                    const displayValue = shouldBlur ? replaceDigitsWithZeros(data.value) : data.value;
                    return (
                        <div key={data.title} className={secondaryDataHolderStyle}>
                            <Text variant="body_copy_2" className={dataTitleStyle}>
                                {data.title}
                            </Text>
                            <Text variant="info_txt_1" color="var(--colors-gray-800)" className={cx(dataValueStyle, shouldBlur && blurredSecondaryDataStyle)}>
                                <span dangerouslySetInnerHTML={{__html: displayValue}} />
                            </Text>
                        </div>
                    );
                })}
            </div>

            {isMobile && renderButtons()}
        </div>
    );
};

const placeholderWrapperStyle = css({
    position: "relative",
    width: "96px",
    height: "96px"
});

const boxWrapperStyle = css({
    backgroundColor: "var(--colors-white)",
    shadow: "",
    width: "100%",
    padding: 2,
    borderRadius: 2,
    md: {
        padding: 3
    }
});

const boxHeaderWrapperStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    flexDirection: "row",
    gap: 1,
    mb: 2
});

const imageStyle = css({
    minWidth: "96px"
});

const boxHeaderTextsStyle = css({
    display: "flex",
    flexDirection: "column"
});

const buttonsWrapperStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: "column-reverse",
    marginBottom: 3,
    md: {
        flexDirection: "row",
        marginBottom: 0
    }
});

const detailsButtonStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer"
});

const detailsTextStyle = css({
    marginRight: 1
});

const dataTitleStyle = css({
    md: {
        width: "33%"
    }
});

const dataValueStyle = css({
    md: {
        width: "66%"
    }
});

const secondaryDataHolderStyle = css({
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    flexDirection: "column",
    width: "100%",
    borderBottom: "1px solid var(--colors-gray-500)",
    paddingY: 1,
    paddingX: 0,
    gap: 0.5,
    md: {
        alignItems: "center",
        flexDirection: "row"
    }
});

const blurredSecondaryDataStyle = css({
    filter: "blur(6px)",
    userSelect: "none"
});

const buttonsHolderStyle = css({
    display: "inline-flex",
    flexDirection: "column",
    width: "100%",
    md: {
        flexDirection: "row",
        width: "50%"
    }
});

const btnStyle = css({
    width: "100%",
    md: {
        width: "unset"
    }
});

const btnMarginStyle = css({
    marginBottom: 2,
    md: {
        marginRight: 2,
        marginBottom: 0
    }
});

const secondaryDataWrapperStyle = css({
    transition: "max-height 0.4s ease-out",
    overflow: "hidden",
    "& > div:first-of-type": {
        marginTop: 4
    },
    "& div:last-child": {
        borderBottom: "unset",
        marginBottom: 4,
        md: {
            marginBottom: 0
        }
    }
});

const secondaryDataWrapperVisibleStyle = css({
    maxHeight: "500px"
});

const secondaryDataWrapperHiddenStyle = css({
    maxHeight: 0
});

const detailsArrowIconVisibleStyle = css({
    transform: "rotate(0deg)",
    transition: "transform 0.4s linear"
});

const detailsArrowIconHiddenStyle = css({
    transform: "rotate(180deg)",
    transition: "transform 0.4s linear"
});
